/**
 * A synthetic RGB face with several regions.
 *
 * The single-channel `SyntheticSubject` was enough to test a green-only
 * estimator. It cannot test the two things that actually matter now: whether
 * chrominance extraction rejects motion, and whether fusing regions beats
 * trusting one. Both require a subject that has channels and regions to begin
 * with.
 *
 * The physical model, stated plainly so it can be argued with:
 *
 *   Melanin absorbs more strongly at shorter wavelengths — roughly as λ⁻³ — so
 *   blue is attenuated hardest, green nearly as hard, red least. That is why
 *   the green channel, which carries the most pulse, is also the one melanin
 *   takes away.
 *
 *   Haemoglobin's pulsatile absorption peaks near 540 nm, so the green channel
 *   carries the strongest cardiac modulation, blue roughly half, red under a
 *   third.
 *
 *   Motion and illumination changes scale ALL channels together. That is
 *   precisely the property chrominance methods exploit, and precisely why a
 *   green-only estimator cannot tell them from a pulse.
 *
 * These coefficients reproduce the right relationships and orders of magnitude.
 * They are not fitted to a measured dataset, and the moment real skin-stratified
 * validation data exists they should be replaced by it.
 */

import { Rng } from './subject.js';
import { melanin } from './monk.js';

/** Melanin attenuation exponent per channel — shorter wavelength, stronger absorption. */
export const MELANIN_DECAY = Object.freeze({ r: 1.2, g: 3.0, b: 3.6 });

/** Pulsatile modulation carried by each channel, relative to green. */
export const PULSE_GAIN = Object.freeze({ r: 0.30, g: 1.0, b: 0.55 });

/** Baseline reflectance of lightly-pigmented skin, in 8-bit camera units. */
export const BASE_REFLECTANCE = Object.freeze({ r: 190, g: 150, b: 135 });

export const FACE_DEFAULTS = Object.freeze({
  fs: 30,
  regions: 3,
  /** Amplitude of head motion / illumination drift, as a fraction of DC. 0 = perfectly still. */
  motion: 0,
  /** Mains-frequency light flicker, as a fraction of DC. */
  flicker: 0,
  noiseSigma: 0.55,
  respHz: 0.25,
  respAmplitude: 0.35,
  secondHarmonic: 0.35,
  hrvJitter: 0.00035,
  /** Relative perfusion of each region: forehead is best, cheeks slightly less. */
  regionPerfusion: [1.0, 0.82, 0.76],
  /** Pulse transit differences across the face, in seconds. Small but real. */
  regionPhaseLag: [0, 0.012, 0.015],
});

export class SyntheticFace {
  constructor(options = {}) {
    this.cfg = { ...FACE_DEFAULTS, ...options };
    this.bpm = options.bpm ?? 72;
    this.tone = options.tone ?? 1;
    this.rng = new Rng(options.seed ?? 12345);
    this.phase = 0;
    this.i = 0;
    this.motionState = 0;

    const m = melanin(this.tone);
    this.attenuation = {
      r: Math.exp(-MELANIN_DECAY.r * m),
      g: Math.exp(-MELANIN_DECAY.g * m),
      b: Math.exp(-MELANIN_DECAY.b * m),
    };
    this.dc = {
      r: BASE_REFLECTANCE.r * this.attenuation.r,
      g: BASE_REFLECTANCE.g * this.attenuation.g,
      b: BASE_REFLECTANCE.b * this.attenuation.b,
    };
  }

  /** Pulse waveform at a given phase — systolic peak plus a dicrotic wave. */
  static wave(p, secondHarmonic) {
    return Math.sin(2 * Math.PI * p) + secondHarmonic * Math.sin(4 * Math.PI * p);
  }

  /**
   * One frame.
   * @returns {Array<{r:number,g:number,b:number}>} one entry per region
   */
  nextFrame() {
    const c = this.cfg;
    const t = this.i / c.fs;

    this.phase += this.bpm / 60 / c.fs + this.rng.gauss() * c.hrvJitter;

    // Motion and flicker are multiplicative and affect every channel equally.
    // A green-only estimator has no way to distinguish this from a heartbeat.
    this.motionState = this.motionState * 0.96 + this.rng.gauss() * c.motion;
    const flicker = c.flicker * Math.sin(2 * Math.PI * 1.7 * t);
    const intensity = 1 + this.motionState + flicker;

    const respiration = c.respAmplitude * Math.sin(2 * Math.PI * c.respHz * t);

    const frame = [];
    for (let region = 0; region < c.regions; region++) {
      const perfusion = c.regionPerfusion[region % c.regionPerfusion.length];
      const lag = c.regionPhaseLag[region % c.regionPhaseLag.length];
      const p = (this.phase - lag * (this.bpm / 60)) % 1;
      const wave = SyntheticFace.wave(p < 0 ? p + 1 : p, c.secondHarmonic);

      const pixel = {};
      for (const ch of ['r', 'g', 'b']) {
        const pulse = wave * perfusion * PULSE_GAIN[ch] * this.attenuation[ch];
        const noise = this.rng.gauss() * c.noiseSigma;
        pixel[ch] = this.dc[ch] * intensity + pulse + respiration * 0.2 + noise;
      }
      frame.push(pixel);
    }

    this.i++;
    return frame;
  }

  /** Generate `n` frames as per-region channel arrays, ready for an extractor. */
  take(n) {
    const regions = Array.from({ length: this.cfg.regions }, () => ({
      r: new Float64Array(n),
      g: new Float64Array(n),
      b: new Float64Array(n),
    }));
    for (let i = 0; i < n; i++) {
      const frame = this.nextFrame();
      for (let k = 0; k < frame.length; k++) {
        regions[k].r[i] = frame[k].r;
        regions[k].g[i] = frame[k].g;
        regions[k].b[i] = frame[k].b;
      }
    }
    return regions;
  }
}

export function generateFace(options = {}) {
  const { samples = 1200, ...rest } = options;
  return new SyntheticFace(rest).take(samples);
}
